import React, { useEffect, useSyncExternalStore } from 'react';
import {
  DEFAULTS,
  SETTINGS,
  getSettings,
  resetSettings,
  setSetting,
  subscribe
} from './settings.mjs';

/*
 * Live settings, from anywhere in the tree.
 *
 * `useSyncExternalStore` rather than context: the readers — the sky renderer,
 * the round reveal, the results summary, the page's own palette class — are
 * scattered, and every component between them would otherwise have to carry a
 * prop it does not use. The store hands back a cached snapshot, so this is a
 * stable reference and the subscription does not re-render on every tick.
 */
export function useSettings() {
  return useSyncExternalStore(subscribe, getSettings, () => DEFAULTS);
}

/*
 * An ordered axis, dragged rather than picked from. The brightness presets are a
 * single run from generous to harsh — a test in `test.js` pins that ordering —
 * so a slider says what five separate buttons could not: that these are points
 * along one dial, not five unrelated options.
 */
function SettingSlider({ name, spec, value }) {
  const index = Math.max(0, spec.values.indexOf(value));
  const current = spec.options[index];

  return (
    <div className="sg-setting-slider">
      <input
        type="range"
        min={0}
        max={spec.options.length - 1}
        step={1}
        value={index}
        aria-label={spec.label}
        aria-valuetext={current.label}
        onChange={(event) => setSetting(name, spec.values[Number(event.target.value)])}
      />
      <div className="sg-setting-ticks" aria-hidden="true">
        {spec.options.map((option) => (
          <span key={option.value} className={option.value === value ? 'is-current' : ''}>
            {option.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function SettingChoices({ name, spec, value }) {
  return (
    <div className="sg-setting-choices">
      {spec.options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={option.value === value ? 'is-current' : ''}
          onClick={() => setSetting(name, option.value)}
          aria-pressed={option.value === value}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/*
 * The settings overlay. Same furniture as the rules sheet: fixed over the page,
 * closes on Escape and on a backdrop click, scrolls inside itself if the window
 * is short. Rendered once by the app rather than per screen, so the same panel
 * opens over the start page, a round and the results.
 *
 * No explanatory prose. Every row is a name and its options, which is all a
 * settings list has ever needed to be.
 */
export default function SettingsMenu({ onClose }) {
  const settings = useSettings();

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isDefault = Object.keys(SETTINGS).every((key) => settings[key] === DEFAULTS[key]);

  return (
    <div
      className="sg-how-veil"
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
      onClick={onClose}
    >
      <div className="sg-how-sheet" onClick={(event) => event.stopPropagation()}>
        <button className="sg-how-close" type="button" onClick={onClose} aria-label="Close">
          &times;
        </button>
        <h2>Settings</h2>

        <dl className="sg-settings">
          {Object.entries(SETTINGS).map(([name, spec]) => (
            <div className="sg-setting" key={name}>
              <dt>{spec.label}</dt>
              <dd>
                {spec.slider ? (
                  <SettingSlider name={name} spec={spec} value={settings[name]} />
                ) : (
                  <SettingChoices name={name} spec={spec} value={settings[name]} />
                )}
                {spec.note && <p className="sg-setting-note">{spec.note}</p>}
              </dd>
            </div>
          ))}
        </dl>

        <button
          className="sg-setting-reset"
          type="button"
          onClick={resetSettings}
          disabled={isDefault}
        >
          Reset to defaults
        </button>
      </div>
    </div>
  );
}
